import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const AxiosContext = createContext();

export const useAxios = () => {
  const context = useContext(AxiosContext);
  if (!context) {
    throw new Error('useAxios must be used within an AxiosProvider');
  }
  return context;
};

export const AxiosProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refresh_token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Debug environment variable loading
  console.log('🔍 Environment variable check:');
  console.log('  REACT_APP_API_BASE_URL:', process.env.REACT_APP_API_BASE_URL);
  console.log('  All process.env:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

  // Create axios instance with configurable base URL
  const api = axios.create({
    baseURL: process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  console.log('🌐 Final API baseURL being used:', api.defaults.baseURL);

  // Request interceptor to add auth token
  api.interceptors.request.use(
    (config) => {
      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor to handle token refresh
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        if (refreshToken) {
          try {
            const response = await axios.post('/api/accounts/token/refresh/', {
              refresh: refreshToken,
            });

            const newAccessToken = response.data.access;
            setAccessToken(newAccessToken);
            localStorage.setItem('access_token', newAccessToken);

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            // Refresh failed, logout user
            logout();
          }
        } else {
          logout();
        }
      }

      return Promise.reject(error);
    }
  );

  // Auth functions
  const login = async (email, password) => {
    try {
      console.log('🚀 Starting login process...');
      const response = await api.post('/accounts/token/', { email, password });
      const { access, refresh } = response.data;
      console.log('🎟️ Tokens received:', { access: access?.substring(0, 20) + '...', refresh: refresh?.substring(0, 20) + '...' });

      setAccessToken(access);
      setRefreshToken(refresh);
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      console.log('💾 Tokens stored in localStorage');

      console.log('👤 Fetching user data...');
      await fetchUser(access);
      console.log('✅ Login process completed successfully');
      return { success: true };
    } catch (error) {
      console.error('❌ Login error:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (email, password, firstName = '', lastName = '') => {
    console.log('🔥 Register function called with:', { email, firstName, lastName });
    console.log('🌐 API baseURL:', api.defaults.baseURL);
    
    try {
      console.log('📡 Making POST request to /accounts/registration/');
      const response = await api.post('/accounts/registration/', {
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });

      console.log('✅ Registration API response:', response.data);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('❌ Registration API error:', error);
      console.error('📄 Error response:', error.response?.data);
      console.error('🔢 Error status:', error.response?.status);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    console.log('🚪 Logout called - clearing tokens and user data');
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  };

  const fetchUser = async (token = null) => {
    try {
      console.log('🔍 Fetching user data...');
      const authToken = token || accessToken;
      console.log('🎟️ Using token:', authToken ? authToken.substring(0, 20) + '...' : 'null');
      
      const response = await api.get('/accounts/dj-rest-auth/user/', {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      console.log('✅ User data fetched successfully:', response.data);
      setUser(response.data);
    } catch (error) {
      console.error('❌ Error fetching user:', error);
      console.error('📄 Error response:', error.response?.data);
      console.error('🔢 Error status:', error.response?.status);
      console.error('🌐 Request URL:', error.config?.url);
      logout();
    }
  };

  const resetPassword = async (email) => {
    try {
      const response = await api.post('/accounts/dj-rest-auth/password/reset/', { email });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Password reset error:', error);
      return { 
        success: false, 
        error: error.response?.data?.email?.[0] || 'Password reset failed' 
      };
    }
  };

  const confirmPasswordReset = async (uid, token, newPassword) => {
    try {
      const response = await api.post('/accounts/dj-rest-auth/password/reset/confirm/', {
        uid,
        token,
        new_password1: newPassword,
        new_password2: newPassword,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Password reset confirm error:', error);
      return { 
        success: false, 
        error: error.response?.data?.new_password1?.[0] || 'Password reset confirmation failed' 
      };
    }
  };

  // Discord server management functions
  const getServers = async () => {
    try {
      const response = await api.get('/discord/servers/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching servers:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to fetch servers' 
      };
    }
  };

  const addServer = async (serverId, serverName = '') => {
    try {
      const response = await api.post('/discord/servers/add/', {
        server_id: serverId,
        server_name: serverName,
      });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error adding server:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to add server' 
      };
    }
  };

  const updateServer = async (serverId, updates) => {
    try {
      const response = await api.put(`/discord/servers/${serverId}/`, updates);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating server:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to update server' 
      };
    }
  };

  const deleteServer = async (serverId) => {
    try {
      const response = await api.delete(`/discord/servers/${serverId}/delete/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error deleting server:', error);
      return { 
        success: false, 
        error: error.response?.data?.error || 'Failed to delete server' 
      };
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      console.log('🔄 checkAuth useEffect triggered:', { 
        accessToken: accessToken?.substring(0, 20) + '...' || 'null',
        hasUser: !!user 
      });
      if (accessToken) {
        console.log('🎟️ Access token found, fetching user...');
        await fetchUser();
      } else {
        console.log('❌ No access token found');
      }
      setLoading(false);
    };

    checkAuth();
  }, [accessToken]);

  const value = {
    // Auth state
    user,
    accessToken,
    refreshToken,
    loading,
    isAuthenticated: !!accessToken && !!user,

    // Auth functions
    login,
    register,
    logout,
    resetPassword,
    confirmPasswordReset,
    fetchUser, // Add fetchUser to allow manual refresh of user data

    // Server management functions
    getServers,
    addServer,
    updateServer,
    deleteServer,

    // Raw axios instance for custom requests
    api,
  };

  return (
    <AxiosContext.Provider value={value}>
      {children}
    </AxiosContext.Provider>
  );
}; 